/**
 * share-chat.js
 * Domain use case for sharing chat transcripts with collaborators.
 */

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function createShareChat({ share, fallbackShare, normaliseRecipient } = {}) {
  if (typeof share !== 'function') {
    throw new Error('share is required');
  }

  const normalise = typeof normaliseRecipient === 'function' ? normaliseRecipient : defaultNormaliseRecipient;
  const fallback = typeof fallbackShare === 'function' ? fallbackShare : null;

  return async function shareChat({ chatId, recipientEmails, includeFull = false } = {}) {
    const normalisedChatId = normaliseChatId(chatId);
    if (!normalisedChatId) {
      throw new Error('chatId is required');
    }

    const recipients = normaliseRecipients(recipientEmails, normalise);
    if (recipients.length === 0) {
      throw new Error('At least one recipient email is required');
    }

    const includeAttachments = Boolean(includeFull);

    try {
      const response = await share({ chatId: normalisedChatId, recipientEmails: recipients, includeFull: includeAttachments });
      return buildResult({
        recipients,
        includeFull: includeAttachments,
        chatId: normalisedChatId,
        response,
        source: 'service',
      });
    } catch (error) {
      if (!fallback) {
        throw error;
      }
      const response = await fallback({
        chatId: normalisedChatId,
        recipientEmails: recipients,
        includeFull: includeAttachments,
        error,
      });
      return buildResult({
        recipients,
        includeFull: includeAttachments,
        chatId: normalisedChatId,
        response,
        source: 'fallback',
        error,
      });
    }
  };
}

function buildResult({ recipients, includeFull, chatId, response, source, error }) {
  const result = {
    recipients,
    includeFull,
    context: { chatId },
    response: response ?? null,
    source,
  };
  if (error) {
    result.error = error;
  }
  return result;
}

function normaliseRecipients(input, normalise) {
  const collected = [];
  const seen = new Set();

  const visit = (value) => {
    if (Array.isArray(value)) {
      value.forEach(visit);
      return;
    }
    if (value == null) {
      return;
    }
    if (typeof value === 'string' && value.includes(',')) {
      value.split(',').forEach(visit);
      return;
    }

    const normalised = normalise(value);
    if (!normalised) {
      return;
    }
    const key = normalised.toLowerCase();
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    collected.push(normalised);
  };

  visit(input);
  return collected;
}

function normaliseChatId(value) {
  if (value == null) {
    return '';
  }
  if (typeof value === 'string') {
    return value.trim();
  }
  return String(value).trim();
}

function defaultNormaliseRecipient(value) {
  const asString = toStringSafe(value, '').trim();
  if (!asString) {
    return null;
  }
  const lower = asString.toLowerCase();
  if (!EMAIL_PATTERN.test(lower)) {
    return null;
  }
  return lower;
}

function toStringSafe(value, fallback = '') {
  if (typeof value === 'string') {
    return value;
  }
  if (value == null) {
    return fallback;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return fallback;
}

if (typeof window !== 'undefined') {
  window.AgileProDomain = window.AgileProDomain || {};
  window.AgileProDomain.share = window.AgileProDomain.share || {};
  window.AgileProDomain.share.createShareChat = createShareChat;
}
